import axios from "axios";

const pyApiBase =
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_PY_API_BASE) ||
  "https://api.bernoullirisk.com";

export const SAFE_HAVEN_SCORECARD_URL = `${String(pyApiBase).replace(
  /\/$/,
  ""
)}/py/api/safeHavenScorecard`;

/**
 * Feature flag: when true, single-asset scorecard should call the Python
 * bootstrap API instead of running Monte Carlo in the browser.
 * Default false until Phase A wiring is validated end-to-end.
 */
export const USE_PY_SAFE_HAVEN_SCORECARD =
  String(process.env.NEXT_PUBLIC_USE_PY_SAFE_HAVEN_SCORECARD || "")
    .toLowerCase() === "true";

/**
 * @param {object} body SafeHavenScorecardRequest-compatible payload
 * @returns {Promise<object>} parsed `data` payload from the API
 */
export async function callSafeHavenScorecard(body = {}) {
  const { data } = await axios.post(SAFE_HAVEN_SCORECARD_URL, body, {
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  });
  const raw = data?.data ?? data;
  return typeof raw === "string" ? JSON.parse(raw) : raw;
}

/**
 * Build request body from common scorecard UI state.
 */
export function buildScorecardRequest({
  baseAsset = "spx",
  otherAsset = "gold",
  fromYear = 1901,
  toYear = 2020,
  regimeCount = 4,
  regimeSelectors,
  pathCount = 1000,
  horizon = 25,
  firstAssetAlloc = 72,
  secondAssetAlloc = 28,
  useRecommendedAlloc = false,
  percentile1 = 50,
  percentile2 = 5,
  cartoon = null,
  seed = 42,
  userId,
  mode = "normal",
} = {}) {
  const payload = {
    baseAsset,
    otherAsset,
    fromYear,
    toYear,
    frequency: "a",
    regimeCount,
    mode,
    pathCount,
    horizon,
    alloc: { firstAssetAlloc, secondAssetAlloc },
    useRecommendedAlloc: Boolean(useRecommendedAlloc),
    percentile1,
    percentile2,
    cartoon,
    seed,
  };
  if (Array.isArray(regimeSelectors) && regimeSelectors.length > 0) {
    payload.regimeSelectors = regimeSelectors;
  }
  if (userId !== undefined && userId !== null) payload.user_id = userId;
  return payload;
}

export function buildScorecardBatchRequest({
  shared = {},
  columns = [],
} = {}) {
  const sharedPayload = {
    baseAsset: shared.baseAsset ?? "spx",
    fromYear: shared.fromYear ?? 1901,
    toYear: shared.toYear ?? 2020,
    frequency: shared.frequency ?? "a",
    regimeCount: shared.regimeCount ?? 4,
    mode: shared.mode ?? "normal",
    pathCount: shared.pathCount ?? 1000,
    horizon: shared.horizon ?? 25,
    lockAlloc: Boolean(shared.lockAlloc),
    seed: shared.seed ?? 42,
  };

  if (Array.isArray(shared.regimeSelectors) && shared.regimeSelectors.length > 0) {
    sharedPayload.regimeSelectors = shared.regimeSelectors;
  }
  if (shared.userId !== undefined && shared.userId !== null) {
    sharedPayload.user_id = shared.userId;
  }

  return {
    shared: sharedPayload,
    columns: columns.map((column) => ({
      columnId: column.columnId,
      otherAsset: column.otherAsset ?? "gold",
      alloc: {
        firstAssetAlloc: column.firstAssetAlloc ?? 72,
        secondAssetAlloc: column.secondAssetAlloc ?? 28,
      },
      percentile1: column.percentile1 ?? 50,
      percentile2: column.percentile2 ?? 5,
      cartoon: column.cartoon ?? null,
    })),
  };
}
